import React from "react";
import { motion } from "framer-motion";
import { Moon, Cloud, Palette, Sparkles, Lock, CreditCard, Heart } from "lucide-react";
import { TeddyBear } from "./icons/TeddyBear";
import { useGender } from "@/contexts/GenderContext";
import { Link } from "wouter";

interface NonSubscriberLoadingAnimationProps {
  message?: string;
  showUpgradePrompt?: boolean;
}

export function NonSubscriberLoadingAnimation({
  message = "Unlocking premium features...",
  showUpgradePrompt = true,
}: NonSubscriberLoadingAnimationProps) {
  const { selectedGender } = useGender();

  const getLogoSrc = () => {
    switch (selectedGender) {
      case "girl":
        return "/logo-pink.png";
      case "boy":
        return "/logo-blue.png";
      case "other":
        return "/logo-purple.png";
      default:
        return "/logo.png";
    }
  };

  return (
    <div className="flex flex-col items-center justify-center p-8 space-y-6">
      {/* Main animated icon with lock overlay */}
      <motion.div
        className="relative"
        animate={{
          scale: [1, 1.1, 1],
          rotate: [0, 10, -10, 0],
        }}
        transition={{
          duration: 3,
          ease: "easeInOut",
          repeat: Infinity,
        }}
      >
        <img
          src={getLogoSrc()}
          alt="SnuggleCrafters Logo"
          className="w-24 h-24 object-contain"
          data-testid="loading-logo"
        />

        {/* Lock icon overlay */}
        <motion.div
          className="absolute -top-2 -right-2 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full p-2 shadow-lg"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 5, -5, 0],
          }}
          transition={{
            duration: 2,
            ease: "easeInOut",
            repeat: Infinity,
          }}
        >
          <Lock className="w-6 h-6 text-white" />
        </motion.div>

        {/* Floating sparkles around the icon */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            initial={{
              opacity: 0,
              scale: 0,
              x: 0,
              y: 0,
            }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
              x: [0, (i % 2 === 0 ? 1 : -1) * (30 + i * 10)],
              y: [0, -30 - i * 5, -60 - i * 10],
            }}
            transition={{
              duration: 2,
              delay: i * 0.3,
              repeat: Infinity,
              ease: "easeOut",
            }}
            style={{
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          >
            <img
              src={getLogoSrc()}
              alt="SnuggleCrafters Logo"
              className="w-4 h-4 object-contain opacity-70"
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Progress message */}
      <motion.h3
        className="text-xl font-medium text-primary"
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 2, repeat: Infinity }}
        data-testid="progress-message"
      >
        {message}
      </motion.h3>

      {/* Premium features preview */}
      <motion.div 
        className="w-full max-w-md space-y-4"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.5 }}
      >
        <div className="bg-gradient-to-r from-purple-50 to-teal-50 rounded-2xl p-6 border border-purple-200">
          <div className="flex items-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-purple-600" />
            <h4 className="font-semibold text-purple-800">Premium Features</h4>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-teal-500 rounded-full"></div>
              <span className="text-sm text-gray-700">Unlimited story generation</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-teal-500 rounded-full"></div>
              <span className="text-sm text-gray-700">AI-powered narration</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-teal-500 rounded-full"></div>
              <span className="text-sm text-gray-700">Beautiful illustrations</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-gradient-to-r from-purple-500 to-teal-500 rounded-full"></div>
              <span className="text-sm text-gray-700">Physical book ordering</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Teddy jumping over moon animation */}
      <div className="relative w-full h-32 overflow-hidden">
        {/* Moon */}
        <motion.div
          className="absolute left-1/2 top-8"
          initial={{ scale: 0.8, opacity: 0.5 }}
          animate={{
            scale: [0.8, 1, 0.8],
            opacity: [0.5, 0.8, 0.5],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          style={{
            transform: "translateX(-50%)",
          }}
        >
          <Moon className="w-16 h-16 text-yellow-200 fill-yellow-100" />
        </motion.div>

        {/* Teddy Bear jumping animation */}
        <motion.div
          className="absolute"
          initial={{
            x: -50,
            y: 60,
            rotate: -15,
          }}
          animate={{
            x: [-50, 100, 250, 400],
            y: [60, 0, 0, 60],
            rotate: [-15, -360, -720, -735],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.3, 0.7, 1],
            repeatDelay: 1,
          }}
        >
          {/* Teddy SVG */}
          <img
            src="/teddy.svg"
            alt="Teddy Bear"
            width="40"
            height="40"
            className="text-amber-600"
          />
        </motion.div>

        {/* Stars scattered around */}
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute"
            initial={{ opacity: 0 }}
            animate={{
              opacity: [0, 1, 0],
              scale: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 2,
              delay: i * 0.4,
              repeat: Infinity,
            }}
            style={{
              left: `${20 + i * 15}%`,
              top: `${10 + (i % 2) * 30}px`,
            }}
          >
            <TeddyBear className="w-3 h-3 text-yellow-300 fill-yellow-200" />
          </motion.div>
        ))}
      </div>

      {/* Progress dots */}
      <div className="flex space-x-2">
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="w-3 h-3 bg-gradient-to-r from-purple-500 to-teal-500 rounded-full"
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 1, 0.3],
            }}
            transition={{
              duration: 1.5,
              delay: i * 0.2,
              repeat: Infinity,
            }}
          />
        ))}
      </div>

      {/* Upgrade prompt */}
      {showUpgradePrompt && (
        <motion.div
          className="w-full max-w-md space-y-4"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1 }}
        >
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-6 border border-amber-200">
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="p-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full">
                  <Heart className="w-6 h-6 text-white" />
                </div>
              </div>
              
              <h4 className="text-lg font-bold text-gray-800">
                Unlock Premium Features
              </h4>
              
              <p className="text-sm text-gray-600">
                Get unlimited access to all our magical storytelling features
              </p>
              
              <div className="flex gap-3">
                <Link href="/subscribe">
                  <motion.button
                    className="flex-1 bg-gradient-to-r from-purple-600 to-teal-600 text-white py-3 px-4 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 hover:shadow-lg flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <CreditCard className="w-4 h-4" />
                    Subscribe Now
                  </motion.button>
                </Link>
                
                <Link href="/pricing">
                  <motion.button
                    className="flex-1 bg-white text-purple-600 border-2 border-purple-200 py-3 px-4 rounded-xl font-medium transition-all duration-200 transform hover:scale-105 hover:shadow-lg flex items-center justify-center gap-2"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Palette className="w-4 h-4" />
                    View Plans
                  </motion.button>
                </Link>
              </div>
              
              <p className="text-xs text-gray-500">
                Starting at $6.99/month • Cancel anytime
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Encouraging message */}
      <motion.p
        className="text-sm text-muted-foreground text-center max-w-md"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5 }}
        data-testid="encouragement-message"
      >
        Your magical storytelling journey awaits! Upgrade to unlock unlimited stories, beautiful illustrations, and premium features.
      </motion.p>
    </div>
  );
}